import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { Event } from '../types';
import { showToast } from '../utils/toast';

type DbEvent = Database['public']['Tables']['events']['Row'];
type DbEventInsert = Database['public']['Tables']['events']['Insert'];
type DbEventUpdate = Database['public']['Tables']['events']['Update'];

const isValidUUID = (id: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
const safeUUID = (id?: string | null) => (id && isValidUUID(id) ? id : null);

type DbEventWithRelations = DbEvent & { event_contacts?: { contact_id: string }[] };

const localDateString = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const localTimeString = (date: Date) => {
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${min}`;
};

const mapToEvent = (e: DbEventWithRelations): Event => {
  const startDate = new Date(e.start_time);
  const endDate = new Date(e.end_time);
  return {
    id: e.id,
    title: e.title,
    description: e.description || undefined,
    date: localDateString(startDate),
    time: localTimeString(startDate),
    endDate: localDateString(endDate),
    endTime: localTimeString(endDate),
    type: (e.event_type as Event['type']) || 'meeting',
    location: e.location || '',
    domainId: e.domain_id || '',
    contactIds: e.event_contacts?.map((ec) => ec.contact_id) || [],
    linkedEntityId: e.contact_id || undefined,
    tags: e.tags || [],
    budget: e.budget ?? null,
    goalId: e.goal_id ?? null,
  };
};

export const eventService = {
  async getEvents(): Promise<Event[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Vous devez être connecté.');

      const { data, error } = await supabase
        .from('events')
        .select('*, event_contacts(contact_id)')
        .order('start_time', { ascending: true });

      if (error) {
        console.error('[event.service] getEvents:', error.message);
        showToast('Erreur lors du chargement des événements', 'error');
        return [];
      }
      return (data || []).map(mapToEvent);
    } catch (err: any) {
      console.error('[event.service] getEvents:', err.message);
      showToast('Erreur lors du chargement des événements', 'error');
      return [];
    }
  },

  async createEvent(event: Partial<Event>): Promise<Event> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    // Parse date and time to ISO
    let startTime = new Date().toISOString();
    let endTime = new Date(new Date().getTime() + 60 * 60 * 1000).toISOString();

    if (event.date) {
      const [year, month, day] = event.date.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day);
      if (event.time) {
        const [hours, minutes] = event.time.split(':').map(Number);
        dateObj.setHours(hours, minutes);
      }
      startTime = dateObj.toISOString();
      // Setup 1h event default duration
      endTime = new Date(dateObj.getTime() + 60 * 60 * 1000).toISOString();
    }

    // Override end time if explicitly provided
    if (event.endDate || event.endTime) {
      const baseEndDate = event.endDate || event.date || localDateString(new Date());
      const [ey, em, ed] = baseEndDate.split('-').map(Number);
      const endDateObj = new Date(ey, em - 1, ed);
      if (event.endTime) {
        const [eh, emin] = event.endTime.split(':').map(Number);
        endDateObj.setHours(eh, emin, 0, 0);
      }
      endTime = endDateObj.toISOString();
    }

    const insertData: DbEventInsert = {
      user_id: user.id,
      title: event.title || 'Nouvel événement',
      description: event.description || null,
      domain_id: safeUUID(event.domainId),
      start_time: startTime,
      end_time: endTime,
      end_date: event.endDate || null,
      event_type: event.type || 'meeting',
      location: event.location || null,
      tags: event.tags || [],
      budget: event.budget ?? null,
      goal_id: safeUUID(event.goalId),
    };

    const { data, error } = await supabase
      .from('events')
      .insert(insertData)
      .select('*, event_contacts(contact_id)')
      .single();

    if (error) {
      console.error('[event.service] createEvent:', error.message);
      showToast("Impossible de créer l'événement", 'error');
      if (error.code === '23503') {
        throw new Error(
          "Impossible d'enregistrer l'événement : le domaine lié n'existe pas encore dans la base de données Supabase."
        );
      }
      throw error;
    }

    if (event.contactIds && event.contactIds.length > 0) {
      const relations = event.contactIds.map((contactId) => ({
        event_id: data.id,
        contact_id: contactId,
        user_id: user.id,
      }));
      const { error: relError } = await supabase.from('event_contacts').insert(relations);
      if (relError) console.error('Could not insert event_contacts', relError);
      data.event_contacts = relations;
    }

    return mapToEvent(data);
  },

  async updateEvent(id: string, updates: Partial<Event>): Promise<Event> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    const updateData: DbEventUpdate = {
      ...(updates.title !== undefined && { title: updates.title }),
      ...(updates.description !== undefined && { description: updates.description }),
      ...(updates.domainId !== undefined && { domain_id: safeUUID(updates.domainId) }),
      ...(updates.tags !== undefined && { tags: updates.tags }),
      updated_at: new Date().toISOString(),
    };

    if (updates.date !== undefined || updates.time !== undefined) {
      // Retrieve existing starting time as base if we don't have updates for both
      let baseDateStr = updates.date;
      let baseTimeStr = updates.time;

      // If we only have partial updates, fetch current record to avoid losing date/time
      if (baseDateStr === undefined || baseTimeStr === undefined) {
        const { data: currentEvent } = await supabase
          .from('events')
          .select('start_time')
          .eq('id', id)
          .single();
        if (currentEvent) {
          const existingDate = new Date(currentEvent.start_time);
          if (baseDateStr === undefined) baseDateStr = localDateString(existingDate);
          if (baseTimeStr === undefined) baseTimeStr = localTimeString(existingDate);
        }
      }

      const dateObj = new Date();
      if (baseDateStr) {
        const [year, month, day] = baseDateStr.split('-').map(Number);
        dateObj.setFullYear(year, month - 1, day);
      }
      if (baseTimeStr) {
        const [hours, minutes] = baseTimeStr.split(':').map(Number);
        dateObj.setHours(hours, minutes, 0, 0);
      }

      updateData.start_time = dateObj.toISOString();
      // Default end = start + 1h (overridden below if explicit end provided)
      updateData.end_time = new Date(dateObj.getTime() + 60 * 60 * 1000).toISOString();
    }

    // Handle explicit end date/time
    if (updates.endDate !== undefined || updates.endTime !== undefined) {
      let baseEndDate = updates.endDate;
      let baseEndTime = updates.endTime;

      if (baseEndDate === undefined || baseEndTime === undefined) {
        const { data: currentEvent } = await supabase
          .from('events')
          .select('end_time')
          .eq('id', id)
          .single();
        if (currentEvent) {
          const existingEnd = new Date(currentEvent.end_time);
          if (baseEndDate === undefined) baseEndDate = localDateString(existingEnd);
          if (baseEndTime === undefined) baseEndTime = localTimeString(existingEnd);
        }
      }

      const endDateObj = new Date();
      if (baseEndDate) {
        const [y, m, d] = baseEndDate.split('-').map(Number);
        endDateObj.setFullYear(y, m - 1, d);
      }
      if (baseEndTime) {
        const [h, min] = baseEndTime.split(':').map(Number);
        endDateObj.setHours(h, min, 0, 0);
      }
      updateData.end_time = endDateObj.toISOString();
      updateData.end_date = updates.endDate || null;
    }

    if (updates.type !== undefined) updateData.event_type = updates.type;
    if (updates.location !== undefined) updateData.location = updates.location;
    if (updates.budget !== undefined) updateData.budget = updates.budget ?? null;
    if (updates.goalId !== undefined) updateData.goal_id = safeUUID(updates.goalId);

    const { data, error } = await supabase
      .from('events')
      .update(updateData)
      .eq('id', id)
      .select('*, event_contacts(contact_id)')
      .single();

    if (error) {
      console.error('[event.service] updateEvent:', error.message);
      showToast("Impossible de mettre à jour l'événement", 'error');
      if (error.code === '23503') {
        throw new Error(
          "Impossible d'enregistrer l'événement : le domaine lié n'existe pas encore dans la base de données Supabase."
        );
      }
      throw error;
    }

    if (updates.contactIds !== undefined) {
      const currentContactIds = data.event_contacts?.map((ec) => ec.contact_id) || [];
      const newContactIds = updates.contactIds || [];

      const toAdd = newContactIds.filter((contactId) => !currentContactIds.includes(contactId));
      const toRemove = currentContactIds.filter((contactId) => !newContactIds.includes(contactId));

      if (toAdd.length > 0) {
        await supabase
          .from('event_contacts')
          .insert(
            toAdd.map((contactId) => ({ event_id: id, contact_id: contactId, user_id: user.id }))
          );
      }
      if (toRemove.length > 0) {
        await supabase
          .from('event_contacts')
          .delete()
          .eq('event_id', id)
          .in('contact_id', toRemove);
      }

      data.event_contacts = newContactIds.map((contactId) => ({ contact_id: contactId }));
    }

    return mapToEvent(data);
  },

  async deleteEvent(id: string): Promise<void> {
    const { error } = await supabase.from('events').delete().eq('id', id);

    if (error) {
      console.error('[event.service] deleteEvent:', error.message);
      showToast("Impossible de supprimer l'événement", 'error');
      throw error;
    }
  },
};
